import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { VehicleByRegistrationNumber } from '@//app/api/protected/vehicle/lookup/registration-number/[registrationNumber]';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { pathPrettifier } from '@//utils/vehicles-search/path-prettifier';
import type { CustomProp } from './useEnhancedProperties';

const useExtraProperties = (vehicle: VehicleByRegistrationNumber | ElasticVehicle) => {
  const t = useTranslations('utils.vehicleGlossary.vehicleProperties.extraProperties');
  const category: CustomProp['category'] = {
    id: 1337,
    name: t('base'),
  };
  const tCommon = useTranslations('other.common');
  const tVehicleGlossary = useTranslations('utils.vehicleGlossary');
  const marketMetadata = useMarketMetadata();
  const prettifyPath = pathPrettifier(marketMetadata.pageLinks.searchPage);

  const resolveValue = (value: any): string => {
    if (value === null || value === undefined || !value.toString()) {
      return tCommon('unknown');
    }
    return value;
  };

  const hasValue = (value: any): boolean =>
    value !== null && value !== undefined && value.toString().trim() !== '';

  const getFuelTypes = () =>
    vehicle.fuelTypes === undefined
      ? vehicle.fuelType
      : typeof vehicle.fuelTypes === 'string'
        ? vehicle.fuelTypes
        : vehicle.fuelTypes.map((x) => x.name).join(', ');

  const extraProperties = useMemo((): (CustomProp & {
    href?: string;
    title?: string;
  })[] => {
    const extra: ((CustomProp & { href?: string; title?: string }) | null)[] = [
      hasValue(vehicle.manufacturer)
        ? {
            key: 'manufacturer',
            name: t('manufacturer.displayName'),
            value: resolveValue(vehicle.manufacturer),
            category,
            href: prettifyPath({ manufacturer: vehicle.manufacturer }),
            title: tCommon('seoLinks.query', {
              query: `${vehicle.manufacturer}`,
            }),
          }
        : null,
      hasValue(vehicle.modelSeries)
        ? {
            key: 'modelSeries',
            name: t('modelSeries.displayName'),
            value: resolveValue(vehicle.modelSeries),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
            }),
            title: tCommon('seoLinks.query', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
            }),
          }
        : null,
      hasValue(vehicle.modelName)
        ? {
            key: 'modelName',
            name: t('version.displayName'),
            value: resolveValue(vehicle.modelName),
            category,
          }
        : null,
      hasValue(vehicle.modelYear)
        ? {
            key: 'modelYear',
            name: t('modelYear.displayName'),
            value: resolveValue(vehicle.modelYear),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              'modelYear.min': vehicle.modelYear.toString(),
              'modelYear.max': vehicle.modelYear.toString(),
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: tVehicleGlossary('modelYear').toLowerCase(),
              propertyValue: vehicle.modelYear.toString(),
            }),
          }
        : null,
      hasValue(vehicle.manufactureYear)
        ? {
            key: 'manufactureYear',
            name: t('manufactureYear.displayName'),
            value: resolveValue(vehicle.manufactureYear),
            category,
          }
        : null,
      hasValue(vehicle.gearboxType)
        ? {
            key: 'gearboxType',
            name: t('gearboxType.displayName'),
            value: resolveValue(vehicle.gearboxType),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              gearboxType: vehicle.gearboxType,
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: tVehicleGlossary('gearboxType').toLowerCase(),
              propertyValue: vehicle.gearboxType.toLocaleLowerCase(),
            }),
          }
        : null,
      hasValue(vehicle.enginePower)
        ? {
            key: 'enginePower',
            name: t('enginePower.displayName'),
            value: resolveValue(vehicle.enginePower),
            unit: t('enginePower.unit'),
            category: {
              id: 6,
              name: t('engineCategory'),
              subCategory: {
                id: 54,
                name: t('engineSubCategory'),
              },
            },
          }
        : null,
      hasValue(vehicle.properties?.colorName)
        ? {
            key: 'colorName',
            name: t('colorName.displayName'),
            value: resolveValue(vehicle.properties?.colorName),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              color: vehicle.properties?.colorName!,
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: tVehicleGlossary('color').toLowerCase(),
              propertyValue: vehicle.properties?.colorName?.toLocaleLowerCase() || '???',
            }),
          }
        : null,
      hasValue(vehicle.registrationNumber)
        ? {
            key: 'registrationNumber',
            name: t('registrationNumber.displayName'),
            value: resolveValue(vehicle.registrationNumber),
            category,
          }
        : null,
      hasValue('vin' in vehicle ? vehicle.vin : 'vinNumber' in vehicle ? vehicle.vinNumber : null)
        ? {
            key: 'vin',
            name: t('vin.displayName'),
            value: resolveValue('vin' in vehicle ? vehicle.vin : vehicle.vinNumber),
            category,
          }
        : null,
      hasValue(getFuelTypes())
        ? {
            key: 'fuelTypes',
            name: t('fuelTypes.displayName'),
            value: resolveValue(getFuelTypes()),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              fuelType: getFuelTypes(),
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: tVehicleGlossary('fuelType').toLocaleLowerCase(),
              propertyValue: getFuelTypes()?.toLocaleLowerCase(),
            }),
          }
        : null,
      hasValue(vehicle.engineBaseType)
        ? {
            key: 'engineBaseType',
            name: t('engineBaseType.displayName'),
            value: resolveValue(vehicle.engineBaseType),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              engineBaseType: vehicle.engineBaseType,
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: t('engineBaseType.displayName').toLocaleLowerCase(),
              propertyValue: vehicle.engineBaseType?.toLocaleLowerCase(),
            }),
          }
        : null,
      hasValue(vehicle.properties?.chassis)
        ? {
            key: 'properties.chassis',
            name: t('chassis.displayName'),
            value: resolveValue(vehicle.properties?.chassis),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              chassis: vehicle.properties?.chassis!,
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: t('chassis.displayName').toLocaleLowerCase(),
              propertyValue: vehicle.properties?.chassis?.toLocaleLowerCase() || '???',
            }),
          }
        : null,
      hasValue(vehicle.properties?.segment)
        ? {
            key: 'properties.segment',
            name: t('segment.displayName'),
            value: resolveValue(vehicle.properties?.segment),
            category,
            href: prettifyPath({
              'properties.segment': vehicle.properties?.segment!,
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: tVehicleGlossary('segment').toLocaleLowerCase(),
              propertyValue: vehicle.properties?.segment?.toLocaleLowerCase() || '???',
            }),
          }
        : null,
      hasValue(vehicle.properties?.annualTax)
        ? {
            key: 'properties.annualTax',
            name: t('annualTax.displayName'),
            value: resolveValue(vehicle.properties?.annualTax),
            unit: tCommon('unitPerYear', { unit: marketMetadata.currencySymbol }),
            category,
          }
        : null,
      hasValue(vehicle.properties?.numberOfDoors)
        ? {
            key: 'properties.numberOfDoors',
            name: t('numberOfDoors.displayName'),
            value: resolveValue(vehicle.properties?.numberOfDoors),
            category,
          }
        : null,
      hasValue(vehicle.properties?.drivingWheel)
        ? {
            key: 'properties.drivingWheel',
            name: t('drivingWheel.displayName'),
            value: resolveValue(vehicle.properties?.drivingWheel),
            category,
            href: prettifyPath({
              manufacturer: vehicle.manufacturer,
              modelSeries: vehicle.modelSeries,
              drivingWheel: vehicle.properties?.drivingWheel!,
            }),
            title: tCommon('seoLinks.property', {
              query: `${vehicle.manufacturer} ${vehicle.modelSeries}`,
              propertyLabel: tVehicleGlossary('drivingWheel').toLowerCase(),
              propertyValue: vehicle.properties?.drivingWheel?.toLocaleLowerCase() || '???',
            }),
          }
        : null,
    ];
    return extra.filter(
      (item): item is CustomProp & { href?: string; title?: string } => item !== null,
    );
  }, [vehicle, t, tCommon, prettifyPath]);

  return extraProperties;
};

export default useExtraProperties;
